import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { TourContext, type TourContextValue } from './tourContext';
import { JOURNEY_TOURS, loadPageTours, pageTourFor as findPageTour } from './tours';
import { driverRunner } from './driverAdapter';
import { markTourSeen, readSeenTours } from './tourStorage';
import type { TourDefinition, TourRunner } from './types';

/** Props for {@link TourProvider}. */
export interface TourProviderProps {
  /** Application subtree that can launch tours. */
  children: ReactNode;
  /**
   * Fixed tour catalogue (tests). When omitted, the provider starts with the
   * static journeys and lazily adds the page tours from their own chunk.
   */
  tours?: ReadonlyArray<TourDefinition>;
  /**
   * Renderer for a tour. Defaults to {@link driverRunner} (`driver.js`); tests
   * inject a mock so the provider's launch/persist/auto-start logic is exercised
   * without a real DOM overlay.
   */
  runner?: TourRunner;
}

/** Longest we wait for a page tour's first anchor to mount after launch. */
const PAGE_TOUR_WAIT_MS = 4000;

/** Interval between anchor checks while waiting. */
const PAGE_TOUR_POLL_MS = 100;

/** Whether the OS asks us to minimise motion. Defensive against absent matchMedia. */
function prefersReducedMotion(): boolean {
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/** The selector of a tour's first element-bearing step, if it has one. */
function firstTargetSelector(tour: TourDefinition): string | undefined {
  return tour.steps.find((step) => step.element !== undefined)?.element;
}

/**
 * Provides the guided-tour controls and owns the single auto-start-once decision
 * (item 7.6).
 *
 * Responsibilities:
 * - Expose `startTour(id)` to launch any tour, marking it "seen" when it ends.
 *   Page tours wait (briefly) for their screen's first anchor to mount.
 * - Expose `pageTourFor(pathname)` so the shell can offer the current page's tour.
 * - Track "seen" state (seeded from {@link readSeenTours}) so the Help Centre
 *   launcher can reflect which tours a user has already taken, reactively.
 * - Auto-start the single {@link TourDefinition.autoStart} tour exactly once for a
 *   new user — but only when the shell it points at is actually mounted (guarded
 *   on the first target element existing), so it never fires on, say, the login
 *   screen.
 *
 * The `driver.js` dependency is injected via `runner` so this component — and its
 * tests — never touch the DOM-mutating library directly.
 */
export function TourProvider({
  children,
  tours: fixedTours,
  runner = driverRunner,
}: TourProviderProps): JSX.Element {
  const [tours, setTours] = useState<ReadonlyArray<TourDefinition>>(() => fixedTours ?? JOURNEY_TOURS);

  // Pull in the page tours after first paint (see loadPageTours for why). A
  // failed chunk load only loses per-screen tours; journeys keep working.
  useEffect(() => {
    if (fixedTours !== undefined) {
      return undefined;
    }
    let active = true;
    loadPageTours()
      .then((pageTours) => {
        if (active) {
          setTours([...JOURNEY_TOURS, ...pageTours]);
        }
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [fixedTours]);

  const [seen, setSeen] = useState<ReadonlyArray<string>>(() => readSeenTours());

  // A tour is in flight — used to avoid stacking a second overlay and to gate the
  // one-shot auto-start.
  const runningRef = useRef(false);

  // Guards the page-tour anchor poll below: if the provider unmounts while a
  // poll is still waiting for its first anchor, the poll must not fire `run()`
  // against a torn-down app.
  const mountedRef = useRef(true);
  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );

  const startTour = useCallback(
    (id: string) => {
      const tour = tours.find((candidate) => candidate.id === id);
      if (tour === undefined) {
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console -- dev-only guardrail for a bad id.
          console.warn(`[tours] Unknown tour id "${id}"`);
        }
        return;
      }
      if (runningRef.current) {
        return;
      }
      runningRef.current = true;
      const run = (): void =>
        runner(tour, {
          reducedMotion: prefersReducedMotion(),
          onClose: () => {
            runningRef.current = false;
            markTourSeen(id);
            setSeen((current) => (current.includes(id) ? current : [...current, id]));
          },
        });

      // A page tour may be launched straight after a navigation (Help Centre →
      // "Open & tour"), before the screen has mounted or its data has loaded.
      // Poll briefly for the first anchor so the tour highlights real content;
      // on timeout run anyway — the runner drops steps whose anchor is absent.
      const target = firstTargetSelector(tour);
      if (tour.route === undefined || target === undefined || document.querySelector(target) !== null) {
        run();
        return;
      }
      const deadline = Date.now() + PAGE_TOUR_WAIT_MS;
      const poll = (): void => {
        if (!mountedRef.current) {
          runningRef.current = false;
          return;
        }
        if (document.querySelector(target) !== null || Date.now() >= deadline) {
          run();
          return;
        }
        window.setTimeout(poll, PAGE_TOUR_POLL_MS);
      };
      poll();
    },
    [tours, runner],
  );

  /** The page tour for the current screen (drives the shell's compass button). */
  const pageTourFor = useCallback(
    (pathname: string) => findPageTour(pathname, tours),
    [tours],
  );

  // One-shot auto-start for brand-new users. Runs after the first commit, by which
  // point the routed shell (and therefore the tour's target elements) is in the
  // DOM if the current route renders it.
  useEffect(() => {
    const auto = tours.find((tour) => tour.autoStart === true);
    if (auto === undefined || seen.includes(auto.id) || runningRef.current) {
      return;
    }
    const target = firstTargetSelector(auto);
    // Only auto-start when the shell the tour points at is actually present.
    if (target !== undefined && document.querySelector(target) === null) {
      return;
    }
    startTour(auto.id);
    // Intentionally run once on mount: re-offering is governed by the persisted
    // "seen" store, not by re-running this effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hasSeen = useCallback((id: string) => seen.includes(id), [seen]);

  const value = useMemo<TourContextValue>(
    () => ({ tours, startTour, hasSeen, pageTourFor }),
    [tours, startTour, hasSeen, pageTourFor],
  );

  return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
}
